import { useAtom, useSetAtom } from 'jotai'
import { Combobox } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { mentionTourTarget } from '@renderer/overlay/Sidepanel/components/day-zero-tour/missions'
import type { MentionableItem } from '../../../../../types'
import { useFocusChatInput } from '../../hooks/use-focus-chat-input'
import { closeMentionsAtom, mentionSearchAtom } from '../atoms'
import { useFilteredMentionData } from '../hooks/use-filtered-mention-data'
import { useSelectMention } from '../hooks/use-select-mention'
import { MentionRowContent } from './mentionable-row'
import { MentionSectionHeading, mentionsPopupClassName } from './mention-list-parts'

/**
 * The menu when nothing was typed into the composer to open it (the @ button): a Combobox with its
 * own search field. Rendered inline in the mentions popover, so it keeps the popover's placement;
 * Base UI owns the highlight, arrow keys and Enter, and the list filters through the same
 * `useFilteredMentionData` the inline menu uses.
 */
export const MentionsCombobox = () => {
  const [mentionSearch, setMentionSearch] = useAtom(mentionSearchAtom)
  const closeMentions = useSetAtom(closeMentionsAtom)
  const focusChatInput = useFocusChatInput()
  const { sections } = useFilteredMentionData()
  const selectMention = useSelectMention()

  const dismiss = () => {
    closeMentions()
    focusChatInput()
  }

  return (
    <Combobox
      inline
      open
      autoHighlight
      items={sections.map((section) => ({ value: section.label, items: section.items }))}
      filter={null}
      value={null}
      inputValue={mentionSearch}
      onInputValueChange={(value) => setMentionSearch(value)}
      // The wrapper isn't generic over the item type, so the value arrives as unknown.
      onValueChange={(value) => {
        const mention = value as MentionableItem | null
        if (!mention) return
        selectMention(mention)
        focusChatInput()
      }}
      itemToStringLabel={(value) => (value as MentionableItem).label}
      className={twMerge(mentionsPopupClassName, 'gap-0 font-normal')}
    >
      <div className="p-1 pb-0">
        <Combobox.Input
          autoFocus
          placeholder="Search models, tabs and tools…"
          aria-label="Search mentions"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault()
              dismiss()
            }
          }}
        />
      </div>
      <Combobox.List className="flex max-h-72 flex-col gap-px overflow-y-auto overscroll-contain p-1">
        {sections.map((section) => (
          <Combobox.Group key={section.label} className="flex flex-col gap-px">
            <Combobox.GroupLabel className="flex justify-between">
              <MentionSectionHeading section={section} />
            </Combobox.GroupLabel>
            {section.items.map((mention) => (
              <Combobox.Item
                key={mention.value}
                value={mention}
                showIndicator={false}
                data-mention-type={mention.type}
                data-mention-value={mention.value}
                data-tour-section={mention.type === 'shortcut' ? mentionTourTarget(mention.label) : undefined}
              >
                <MentionRowContent mention={mention} />
              </Combobox.Item>
            ))}
          </Combobox.Group>
        ))}
      </Combobox.List>
      {sections.length === 0 && (
        <p role="status" className="text-foreground-secondary px-[11px] pb-2.5 pt-1.5 text-sm">
          No matches
        </p>
      )}
    </Combobox>
  )
}
